import { useState, useCallback, useRef, useEffect } from "react";
import type { Observation, FlightSegment, AircraftSummary, TrackPoint } from "./api";
import type { TZ } from "./lib/time";
import { msToDatetimeInput, datetimeInputToMs } from "./lib/time";
import {
  listObservations,
  getTrack,
  getFlightSegments,
  getAircraftSummary,
} from "./api";
import { getViewportCircle } from "./map";
import Sidebar from "./components/Sidebar";
import MapContainer from "./components/MapContainer";

// ---- Constants ------------------------------------------------------------
export const RELATIVE_RANGES: Record<string, number> = {
  "5m":  5 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "1h":  60 * 60 * 1000,
  "6h":  6 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d":  7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

// ---- Types ----------------------------------------------------------------
export interface FormState {
  timeRange: string;
  customStart: string;
  customEnd: string;
  hex: string;
  flight: string;
  registration: string;
  altMin: string;
  altMax: string;
  squawk: string;
  alert: boolean;
  spi: boolean;
  military: boolean;
  onGround: boolean;
  lat: string;
  lon: string;
  radiusNm: string;
}

export interface MapView {
  lat: number;
  lon: number;
  zoom: number;
}

// ---- URL helpers ----------------------------------------------------------
function asNumber(s: string): number | undefined {
  const n = Number(s);
  return s.trim() === "" || isNaN(n) ? undefined : n;
}

function readInitialFormState(tz: TZ): FormState {
  const p = new URLSearchParams(window.location.search);
  const relative = p.get("relative");
  const startMs = p.get("start");
  const endMs = p.get("end");

  let timeRange = "15m";
  let customStart = "";
  let customEnd = "";

  if (relative && RELATIVE_RANGES[relative] !== undefined) {
    timeRange = relative;
  } else if (startMs || endMs) {
    timeRange = "custom";
    if (startMs) customStart = msToDatetimeInput(Number(startMs), tz);
    if (endMs)   customEnd   = msToDatetimeInput(Number(endMs), tz);
  }

  return {
    timeRange,
    customStart,
    customEnd,
    hex:          p.get("hex") ?? "",
    flight:       p.get("flight") ?? "",
    registration: p.get("registration") ?? "",
    altMin:       p.get("alt_min") ?? "",
    altMax:       p.get("alt_max") ?? "",
    squawk:       p.get("squawk") ?? "",
    alert:        p.get("alert") === "true",
    spi:          p.get("spi") === "true",
    military:     p.get("military") === "true",
    onGround:     p.get("on_ground") === "true",
    lat:          p.get("lat") ?? "",
    lon:          p.get("lon") ?? "",
    radiusNm:     p.get("radius_nm") ?? "",
  };
}

function readInitialMapView(): MapView | undefined {
  const p = new URLSearchParams(window.location.search);
  const lat  = asNumber(p.get("map_lat") ?? "");
  const lon  = asNumber(p.get("map_lon") ?? "");
  const zoom = asNumber(p.get("map_zoom") ?? "");
  if (lat !== undefined && lon !== undefined && zoom !== undefined) {
    return { lat, lon, zoom };
  }
  return undefined;
}

function readInitialRadiusCircle(): { lat: number; lon: number; radiusNm: number } | null {
  const p = new URLSearchParams(window.location.search);
  const lat      = asNumber(p.get("lat") ?? "");
  const lon      = asNumber(p.get("lon") ?? "");
  const radiusNm = asNumber(p.get("radius_nm") ?? "");
  if (lat !== undefined && lon !== undefined && radiusNm !== undefined && radiusNm > 0) {
    return { lat, lon, radiusNm };
  }
  return null;
}

const DEFAULT_FORM_STATE: FormState = {
  timeRange: "15m", customStart: "", customEnd: "",
  hex: "", flight: "", registration: "",
  altMin: "", altMax: "", squawk: "",
  alert: false, spi: false, military: false, onGround: false,
  lat: "", lon: "", radiusNm: "",
};

function buildSearchParams(form: FormState, tz: TZ, mapView?: MapView): URLSearchParams {
  const p = new URLSearchParams();
  const add = (k: string, v: string | number | boolean | undefined) => {
    if (v !== undefined && v !== null && v !== "" && v !== false) p.set(k, String(v));
  };

  if (form.timeRange !== "custom") {
    add("relative", form.timeRange);
  } else {
    const startMs = datetimeInputToMs(form.customStart, tz);
    const endMs   = datetimeInputToMs(form.customEnd, tz);
    if (startMs !== undefined) add("start", startMs);
    if (endMs !== undefined)   add("end", endMs);
  }
  add("hex",          form.hex.trim() || undefined);
  add("flight",       form.flight.trim() || undefined);
  add("registration", form.registration.trim() || undefined);
  add("alt_min",      asNumber(form.altMin));
  add("alt_max",      asNumber(form.altMax));
  add("squawk",       form.squawk.trim() || undefined);
  add("alert",        form.alert || undefined);
  add("spi",          form.spi || undefined);
  add("military",     form.military || undefined);
  add("on_ground",    form.onGround || undefined);
  add("lat",          asNumber(form.lat));
  add("lon",          asNumber(form.lon));
  add("radius_nm",    asNumber(form.radiusNm));

  if (mapView) {
    p.set("map_lat",  mapView.lat.toFixed(6));
    p.set("map_lon",  mapView.lon.toFixed(6));
    p.set("map_zoom", String(mapView.zoom));
  }

  return p;
}

function formStateToFilters(form: FormState, tz: TZ) {
  const now = Date.now();
  let start: number | undefined;
  let end: number | undefined;

  if (form.timeRange !== "custom") {
    end   = now;
    start = now - (RELATIVE_RANGES[form.timeRange] ?? RELATIVE_RANGES["15m"]);
  } else {
    start = datetimeInputToMs(form.customStart, tz);
    end   = datetimeInputToMs(form.customEnd, tz);
  }

  return {
    start,
    end,
    hex:          form.hex.trim() || undefined,
    flight:       form.flight.trim() || undefined,
    registration: form.registration.trim() || undefined,
    alt_min:      asNumber(form.altMin),
    alt_max:      asNumber(form.altMax),
    squawk:       form.squawk.trim() || undefined,
    alert:        form.alert || undefined,
    spi:          form.spi || undefined,
    military:     form.military || undefined,
    on_ground:    form.onGround || undefined,
    lat:          asNumber(form.lat),
    lon:          asNumber(form.lon),
    radius_nm:    asNumber(form.radiusNm),
    limit:        5000,
  };
}

// ---- App ------------------------------------------------------------------
export default function App() {
  const [tz, setTz] = useState<TZ>(() => (localStorage.getItem("tz") as TZ) ?? "utc");
  const tzRef = useRef<TZ>(tz);
  useEffect(() => { tzRef.current = tz; }, [tz]);

  const [form, setForm] = useState<FormState>(() =>
    readInitialFormState((localStorage.getItem("tz") as TZ) ?? "utc")
  );
  const [mapView, setMapView] = useState<MapView | undefined>(readInitialMapView);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [viewportAutoActive, setViewportAutoActive] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Detail panel
  type DetailView = "list" | "flights" | "summary";
  const [detailView, setDetailView] = useState<DetailView>("list");
  const [detailHex, setDetailHex] = useState<string | null>(null);
  const [flightSegments, setFlightSegments] = useState<FlightSegment[]>([]);
  const [selectedSummary, setSelectedSummary] = useState<AircraftSummary | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<FlightSegment | null>(null);

  // Track
  const [trackPoints, setTrackPoints] = useState<TrackPoint[]>([]);
  const [trackLabel, setTrackLabel] = useState("");

  // Radius circle for map overlay
  const [radiusCircle, setRadiusCircle] = useState<{ lat: number; lon: number; radiusNm: number } | null>(readInitialRadiusCircle);

  const moveDebounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  const viewportAutoRef = useRef(viewportAutoActive);
  useEffect(() => { viewportAutoRef.current = viewportAutoActive; }, [viewportAutoActive]);

  const mapViewRef = useRef(mapView);
  useEffect(() => { mapViewRef.current = mapView; }, [mapView]);

  // ---- Timezone toggle -----------------------------------------------------
  const handleTzChange = useCallback((newTz: TZ) => {
    setTz(newTz);
    localStorage.setItem("tz", newTz);
    setForm((f) => {
      if (f.timeRange !== "custom") return f;
      const oldTz = tzRef.current;
      const newStart = f.customStart ? (msToDatetimeInput(datetimeInputToMs(f.customStart, oldTz) ?? 0, newTz)) : "";
      const newEnd   = f.customEnd   ? (msToDatetimeInput(datetimeInputToMs(f.customEnd,   oldTz) ?? 0, newTz)) : "";
      return { ...f, customStart: newStart, customEnd: newEnd };
    });
  }, []);

  // ---- Search -------------------------------------------------------------
  const doSearch = useCallback(async (injectViewport = false) => {
    const filters = formStateToFilters(formRef.current, tzRef.current);

    let finalFilters = { ...filters };
    if (injectViewport && (filters.lat == null || filters.lon == null || filters.radius_nm == null)) {
      const vp = getViewportCircle();
      finalFilters = { ...finalFilters, lat: vp.lat, lon: vp.lon, radius_nm: vp.radius_nm };
    }

    setIsSearching(true);
    let page;
    try {
      const TIMEOUT_MS = 15_000;
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS),
      );
      page = await Promise.race([listObservations(finalFilters), timeout]);
    } catch (err) {
      const msg =
        err instanceof Error && err.message === "timeout"
          ? "Search timed out — try a narrower filter"
          : `Search failed: ${err}`;
      setStatusMessage(msg);
      setIsSearching(false);
      return;
    }

    setObservations(page.items);
    setStatusMessage(page.items.length >= 5000 ? "Showing 5,000 results — zoom in for more" : "");
    setViewportAutoActive(true);
    setIsSearching(false);
  }, []);

  // Initial search on mount
  useEffect(() => { doSearch(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Detail panel -------------------------------------------------------
  // Snapshot the detail params from the URL at initialization time, before any
  // effect has a chance to overwrite them (the sync-to-URL effect runs on mount
  // and clears these keys because detailView is still "list" at that point).
  const initialDetailRef = useRef((() => {
    const p = new URLSearchParams(window.location.search);
    return {
      hex: p.get("detail_hex"),
      start: asNumber(p.get("detail_start") ?? ""),
      end: asNumber(p.get("detail_end") ?? ""),
    };
  })());

  const openFlightList = useCallback(async (
    hex: string,
    autoSelect?: { start_at: number; end_at: number },
  ) => {
    setDetailHex(hex);
    setTrackPoints([]);
    setTrackLabel("");
    setSidebarOpen(true);
    try {
      const segs = await getFlightSegments(hex);
      setFlightSegments(segs);
      setDetailView("flights");
      if (autoSelect) {
        const seg = segs.find(
          (s) => s.start_at === autoSelect.start_at && s.end_at === autoSelect.end_at,
        ) ?? { ...autoSelect, callsign: null, position_count: 0 };
        const [summary, points] = await Promise.all([
          getAircraftSummary(hex, seg.start_at, seg.end_at),
          getTrack(hex, seg.start_at, seg.end_at),
        ]);
        setSelectedSummary(summary);
        setSelectedSegment(seg);
        setTrackPoints(points);
        setTrackLabel(seg.callsign ?? hex);
        setDetailView("summary");
      }
    } catch (err) {
      console.error("openFlightList failed:", err);
    }
  }, []);

  const handleSegmentSelect = useCallback(async (segment: FlightSegment) => {
    if (!detailHex) return;
    setTrackPoints([]);
    const [summary, points] = await Promise.all([
      getAircraftSummary(detailHex, segment.start_at, segment.end_at),
      getTrack(detailHex, segment.start_at, segment.end_at),
    ]);
    setSelectedSummary(summary);
    setSelectedSegment(segment);
    setTrackPoints(points);
    setTrackLabel(segment.callsign ?? detailHex);
    setDetailView("summary");
  }, [detailHex]);

  const handleBack = useCallback(() => {
    setTrackPoints([]);
    setTrackLabel("");
    setDetailView("flights");
  }, []);

  const handleClose = useCallback(() => {
    setTrackPoints([]);
    setTrackLabel("");
    setDetailView("list");
  }, []);

  // Sync detail navigation state into the URL
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    p.delete("detail_hex");
    p.delete("detail_start");
    p.delete("detail_end");
    if (detailHex && detailView !== "list") {
      p.set("detail_hex", detailHex);
      if (detailView === "summary" && selectedSegment) {
        p.set("detail_start", String(selectedSegment.start_at));
        p.set("detail_end", String(selectedSegment.end_at));
      }
    }
    history.replaceState(null, "", `?${p}`);
  }, [detailView, detailHex, selectedSegment]);

  // Restore detail navigation from URL on first mount
  useEffect(() => {
    const { hex, start, end } = initialDetailRef.current;
    if (!hex) return;
    openFlightList(hex, start && end ? { start_at: start, end_at: end } : undefined);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Map callbacks -------------------------------------------------------
  const handleMapClick = useCallback((lat: number, lon: number) => {
    const radiusNm = parseFloat(formRef.current.radiusNm);
    setForm((f) => ({ ...f, lat: lat.toFixed(6), lon: lon.toFixed(6) }));
    if (!isNaN(radiusNm) && radiusNm > 0) {
      setRadiusCircle({ lat, lon, radiusNm });
    } else {
      setRadiusCircle(null);
    }
  }, []);

  const handleMapMove = useCallback((lat: number, lon: number, zoom: number) => {
    const newView = { lat, lon, zoom };
    setMapView(newView);
    mapViewRef.current = newView;

    // Persist to URL (preserving existing detail params)
    const p = buildSearchParams(formRef.current, tzRef.current, newView);
    const existing = new URLSearchParams(window.location.search);
    ["detail_hex", "detail_start", "detail_end"].forEach((k) => {
      const v = existing.get(k);
      if (v) p.set(k, v);
    });
    history.replaceState(null, "", `?${p}`);

    // Debounced viewport search
    if (viewportAutoRef.current) {
      clearTimeout(moveDebounceTimer.current);
      moveDebounceTimer.current = setTimeout(() => doSearch(true), 400);
    }
  }, [doSearch]);

  // ---- Search button -------------------------------------------------------
  const handleSearch = useCallback(() => {
    const f = formRef.current;
    const lat = asNumber(f.lat);
    const lon = asNumber(f.lon);
    const radiusNm = asNumber(f.radiusNm);

    if (lat != null && lon != null && radiusNm != null && radiusNm > 0) {
      setRadiusCircle({ lat, lon, radiusNm });
    } else {
      setRadiusCircle(null);
    }

    // Push URL state (preserving existing detail params)
    const p = buildSearchParams(f, tzRef.current, mapViewRef.current);
    const existing = new URLSearchParams(window.location.search);
    ["detail_hex", "detail_start", "detail_end"].forEach((k) => {
      const v = existing.get(k);
      if (v) p.set(k, v);
    });
    history.replaceState(null, "", `?${p}`);

    doSearch(true);
  }, [doSearch]);

  // ---- Reset button --------------------------------------------------------
  const handleReset = useCallback(() => {
    setForm(DEFAULT_FORM_STATE);
    setObservations([]);
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Mobile toggle button — fixed so it's outside Leaflet's event tree */}
      <button
        className="md:hidden fixed bottom-6 left-4 z-[1001] flex items-center justify-center w-10 h-10 rounded-full bg-card border border-border shadow-md"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label="Toggle sidebar"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <Sidebar
        form={form}
        onFormChange={setForm}
        observations={observations}
        onSearch={handleSearch}
        onReset={handleReset}
        onAircraftSelect={openFlightList}
        isSearching={isSearching}
        tz={tz}
        onTzChange={handleTzChange}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        view={detailView}
        hex={detailHex ?? ""}
        segments={flightSegments}
        summary={selectedSummary}
        segment={selectedSegment}
        onSegmentSelect={handleSegmentSelect}
        onDetailBack={handleBack}
        onDetailClose={handleClose}
      />
      <div className="relative flex-1">
        <MapContainer
          initialView={mapView}
          observations={observations}
          trackPoints={trackPoints}
          trackLabel={trackLabel}
          radiusCircle={radiusCircle}
          onObservationSelect={(obs) => openFlightList(obs.hex)}
          onMapClick={handleMapClick}
          onMapMove={handleMapMove}
        />
        {statusMessage && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none whitespace-nowrap rounded-xl bg-background/85 px-4 py-1 text-xs text-foreground">
            {statusMessage}
          </div>
        )}
      </div>
    </div>
  );
}
